<template>
  <b-modal
    :active="showModal"
    :width="450"
  >
    <form
      class="form"
      @submit.prevent="activate"
    >
      <div
        class="modal-card"
        style="width: auto"
      >
        <header class="modal-card-head is-block">
          <span class="icon-static xl large-user" />
          <h4 class="title is-4">
            Your account is waiting for you
          </h4>
        </header>
        <section
          v-if="!complete && loaded"
          class="modal-card-body"
        >
          <h5 class="title is-5">
            Please choose a password
          </h5>

          <div
            v-for="(err, i) in formErrors.non_field_errors"
            :key="i"
            class="message is-danger has-text-left"
          >
            <div class="message-body">
              {{ err }}
            </div>
          </div>
          <div class="field">
            <label class="label">Account</label>
            <div class="control">
              <input
                class="input"
                type="text"
                :value="partialAccountSetup.team.name"
                readonly
              >
            </div>
          </div>
          <div class="field">
            <label class="label">Email address</label>
            <div class="control">
              <input
                class="input"
                type="text"
                :value="partialAccountSetup.user.email"
                readonly
              >
            </div>
          </div>
          <div class="field">
            <label class="label">Password</label>
            <div class="control">
              <input
                id="id_password"
                v-model.trim="activationData.user.password"
                :class="errorClass('user.password')"
                required
                type="password"
                name="password"
                class="input"
                placeholder="Password"
              >
            </div>
            <p
              v-for="(err, i) in getErrors('user.password')"
              :key="i"
              class="help is-danger has-text-left"
            >
              {{ err|capitalize }}
            </p>
          </div>
          <div
            class="field"
            style="padding: 16px 0 0 0"
          >
            <b-checkbox
              v-model="activationData.agreed_to_terms"
            >
              <span
                class="text medium"
                style="padding-left: 6px;"
              >
                I have read and agree to the
                <a
                  href="/terms"
                  target="_blank"
                >Terms and Conditions.</a>
                <br>
                <span
                  class="text color-red"
                  style="padding-left: 12px;"
                >(It is important to have read these thoroughly)</span>
              </span>
            </b-checkbox>
            <br>
            <b-checkbox
              v-model="activationData.has_agreed_to_marketing_email"
            >
              <span
                class="text medium"
                style="padding-left: 6px;"
              >
                Keep me informed about new music, updates and offers.
              </span>
            </b-checkbox>
          </div>
        </section>
        <footer
          v-if="!complete"
          class="modal-card-foot is-block"
        >
          <button
            type="submit"
            :disabled="!activationData.agreed_to_terms"
            class="button is-fullwidth is-large is-primary margin-bottom"
          >
            Activate
          </button>
          <a
            class="text medium"
            @click.prevent="handleShowLogin"
          >Have an account? Log in</a>
        </footer>
        <footer
          v-if="complete"
          class="modal-card-foot"
        />
      </div>
    </form>
  </b-modal>
</template>

<script>
import partialAccountClient from '@/api/partialAccounts.js'
import { formatComponentName } from '@/utils/common.js'
import { logSignup } from '@/utils/segment.js'

export default {
  name: 'ActivateAccountModal',

  props: {
    show: {
      type: Boolean,
      required: true
    },
    partialId: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      loaded: false,
      hideOverride: false,
      partialAccountSetup: null,
      activationData: {
        agreed_to_terms: false,
        client_terms_agreed_at: null,
        has_agreed_to_marketing_email: true,
        user: {
          password: ''
        }
      },
      formErrors: {},
      complete: false
    }
  },

  computed: {
    componentName: function () {
      return formatComponentName(this)
    },

    showModal: function () {
      if (this.hideOverride === true) {
        return false
      }
      return this.show
    }
  },

  created () {
    partialAccountClient.retrieve(this.partialId)
      .then((response) => {
        this.partialAccountSetup = response.data
        this.loaded = true
      })
      .catch(error => {
        if (error.response.status === 404) {
          this.$router.push({ name: 'not-found' })
        }
      })
  },

  methods: {
    activate () {
      this.formErrors = {}

      this.activationData.client_terms_agreed_at = new Date().toISOString()

      partialAccountClient
        .activate(this.partialId, this.activationData)
        .then(() => {
          this.complete = true
          this.login()
          // Clear sensitive data from local store.
          this.activationData.user.password = ''
          logSignup()
        })
        .catch(error => {
          if (error.response.status === 400) {
            this.$set(this, 'formErrors', error.response.data)
            return
          }
          return Promise.reject(error.response)
        })
    },

    login () {
      const email = this.partialAccountSetup.user.email
      const password = this.activationData.user.password

      this.$store.dispatch('auth/login', {
        email,
        password
      })
        .then(() => {
          this.$store.dispatch('common/initAuthenticated')
          this.$store.dispatch('auth/hideLoginModal')
          this.$router.push(this.$store.getters['auth/redirectTo'] || { name: 'track-search' })
          this.$store.dispatch('auth/clearRedirectTo')
        })
        .catch((error) => {
          return Promise.reject(error.response)
        })
    },

    getErrors (path) {
      const parts = path.split('.')
      return parts.reduce((p, c) => (p && p[c]) || null, this.formErrors)
    },

    isValid (path) {
      return this.getErrors(path) === null
    },

    errorClass (path) {
      return !this.isValid(path) ? 'is-danger' : ''
    },

    hide () {
      this.hideOverride = true
    },

    handleShowLogin () {
      this.hide()
      this.$store.dispatch('auth/showLoginModal')
    }
  }
}
</script>

<style lang="scss" scoped>
.success-login {
  text-decoration: underline;
  &:hover {
    text-decoration: none;
  }
}
</style>
